import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, CircularProgress, Button } from '@mui/material';
import { styled } from '@mui/material/styles';

const PreviewFrame = styled('iframe')({
  width: '100%',
  height: '500px',
  border: 'none',
  borderRadius: '4px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
});

const LivePreview = ({ section, refreshTrigger }) => {
  const [loading, setLoading] = useState(true);
  const [previewUrl, setPreviewUrl] = useState('');
  const [error, setError] = useState(null);

  // Get the frontend URL from environment variables or use a default
  const frontendUrl = process.env.REACT_APP_FRONTEND_URL;
  
  useEffect(() => {
    // Set up the preview URL with a section hash if provided
    let url = frontendUrl;
    if (section) {
      url = `${frontendUrl}#${section}`;
    }
    
    // Add a cache-busting parameter to force refresh
    url = `${url}${url.includes('?') ? '&' : '?'}cache=${Date.now()}`;
    
    setPreviewUrl(url);
    
    // Reset loading state when URL changes
    setLoading(true);
    
    // Simulate a short delay to ensure the iframe has time to load
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1500);
    
    return () => clearTimeout(timer);
  }, [frontendUrl, section, refreshTrigger]);

  const handleRefresh = () => {
    setLoading(true);
    // Update URL with new timestamp to force refresh
    setPreviewUrl(`${frontendUrl}${section ? `#${section}` : ''}${frontendUrl.includes('?') ? '&' : '?'}cache=${Date.now()}`);
    
    // Simulate a short delay for loading
    setTimeout(() => {
      setLoading(false);
    }, 1500);
  };

  const handleIframeLoad = () => {
    setLoading(false);
  };

  const handleIframeError = () => {
    setError('Failed to load preview. Please ensure the frontend is running.');
    setLoading(false);
  };

  return (
    <Paper elevation={2} sx={{ p: 2, mb: 3, position: 'relative', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Live Website Preview</Typography>
        <Button 
          variant="outlined" 
          size="small" 
          onClick={handleRefresh}
          disabled={loading}
        >
          Refresh Preview
        </Button>
      </Box>
      
      {error ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="error">{error}</Typography>
          <Button 
            variant="contained" 
            color="primary" 
            sx={{ mt: 2 }}
            onClick={handleRefresh}
          >
            Try Again
          </Button>
        </Box>
      ) : (
        <Box sx={{ position: 'relative' }}>
          {loading && (
            <Box 
              sx={{ 
                position: 'absolute', 
                top: 0, 
                left: 0, 
                right: 0, 
                bottom: 0, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                backgroundColor: 'rgba(255,255,255,0.7)',
                zIndex: 1
              }}
            >
              <CircularProgress />
            </Box>
          )}
          <PreviewFrame 
            src={previewUrl} 
            title="Website Preview" 
            onLoad={handleIframeLoad}
            onError={handleIframeError}
          />
        </Box>
      )}
    </Paper>
  );
};

export default LivePreview;
